import React, { useState } from 'react';
import { db } from '../../firebase/config';
import { collection, addDoc, deleteDoc, doc } from 'firebase/firestore';
import toast from 'react-hot-toast';

const LEVELS = [
    "White Pawn (Level 1)", "Black Knight (Level 2)", 
    "Silver Bishop (Level 3)", "Golden Rook (Level 4)", 
    "Platinum Queen (Level 5)", "Diamond King (Level 6)"
];

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_MAP_TO_INT = { 'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6 };

// Helper to format 24h "17:00" to "5:00 PM to 5:50 PM IST"
const formatTimeRange = (timeStr) => {
    if (!timeStr) return "Time not set";
    let [h, m] = timeStr.split(':');
    h = parseInt(h);
    const startAmPm = h >= 12 ? 'PM' : 'AM';
    const startHr = h % 12 || 12;
    
    // Calculate end time (+50 mins)
    let endDate = new Date();
    endDate.setHours(h, parseInt(m) + 50);
    const endH = endDate.getHours();
    const endM = endDate.getMinutes().toString().padStart(2, '0');
    const endAmPm = endH >= 12 ? 'PM' : 'AM';
    const endHr = endH % 12 || 12;

    return `${startHr}:${m} ${startAmPm} to ${endHr}:${endM} ${endAmPm} IST`;
};

export default function BatchesERP({ allBatches = [], coachesList = [] }) {
    // --- CREATE BATCH STATES ---
    const [batchType, setBatchType] = useState('GROUP'); 
    const [level, setLevel] = useState(LEVELS[0]);
    const [coachEmail, setCoachEmail] = useState('');
    const [selectedDays, setSelectedDays] = useState([]);
    const [time, setTime] = useState('');
    const [studentsStr, setStudentsStr] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // --- SCHEDULER STATES ---
    const [scheduleParams, setScheduleParams] = useState(null);
    const [scheduleConfig, setScheduleConfig] = useState({ totalClasses: 12, startDate: new Date().toISOString().split('T')[0] });

    const handleDayToggle = (day) => {
        setSelectedDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
    };

    const generateBatchTitle = () => {
        if (!coachEmail || selectedDays.length === 0 || !time) return "Fill details to auto-generate title...";
        const coachInfo = coachesList.find(c => c.email === coachEmail);
        const coachName = coachInfo ? (coachInfo.name || 'Coach').split(' ')[0] : 'Coach';
        const shortLevel = level.split(' (')[0];
        const daysStr = selectedDays.join('/');
        let [h, m] = time.split(':'); h = parseInt(h);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const hr12 = h % 12 || 12;
        const typePrefix = batchType === 'PRIVATE' ? '[1-on-1] ' : '';
        return `${typePrefix}${shortLevel} (${coachName}) - ${daysStr} ${hr12}:${m} ${ampm}`;
    };

    const handleCreateBatch = async (e) => {
        e.preventDefault();
        if (selectedDays.length === 0) return toast.error("Please select at least one day!");
        if (!coachEmail || !time) return toast.error("Please fill all fields!");

        setIsSubmitting(true);
        const loadingId = toast.loading("Creating Smart Batch...");

        try {
            const studentArray = studentsStr.split(',').map(s => s.trim().toLowerCase()).filter(s => s);
            await addDoc(collection(db, "batches"), {
                title: generateBatchTitle(),
                batchType: batchType,
                level: level,
                coachEmail: coachEmail, // Always saving as coachEmail
                scheduleDays: selectedDays,
                scheduleTime: time,
                totalClassesCompleted: 0, 
                students: studentArray, 
                createdAt: new Date().toISOString()
            });

            toast.success(`Batch Created!`, { id: loadingId });
            setCoachEmail(''); setSelectedDays([]); setTime(''); setStudentsStr(''); setLevel(LEVELS[0]); setBatchType('GROUP');
        } catch (error) {
            toast.error("Failed to create batch.", { id: loadingId });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (batchId, batchTitle) => {
        if(window.confirm(`Delete "${batchTitle}" permanently?`)) {
            await deleteDoc(doc(db, "batches", batchId));
            toast.success("Batch Deleted!");
        }
    };

    // 🚀 BULK SMART SCHEDULER ENGINE
    const generateSmartSchedule = async () => {
        const { totalClasses, startDate } = scheduleConfig;
        if (totalClasses < 1) return toast.error("Generate at least 1 class!");
        if (!scheduleParams.scheduleDays || scheduleParams.scheduleDays.length === 0) return toast.error("This batch has no days configured!");

        const loadingId = toast.loading(`Generating ${totalClasses} classes in background... ⏳`);
        try {
            let generatedCount = 0;
            let currentDate = new Date(startDate);
            const [hours, minutes] = (scheduleParams.scheduleTime || '18:00').split(':');
            
            const targetDaysInt = scheduleParams.scheduleDays.map(d => DAY_MAP_TO_INT[d]);
            const promises = [];

            while (generatedCount < totalClasses) {
                const dayOfWeek = currentDate.getDay(); // 0 is Sun, 1 is Mon...
                if (targetDaysInt.includes(dayOfWeek)) {
                    const classDate = new Date(currentDate);
                    classDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

                    promises.push(addDoc(collection(db, "classes"), {
                        batchId: scheduleParams.id,
                        batchTitle: scheduleParams.title,
                        teacherId: scheduleParams.coachEmail || scheduleParams.coach, // Safe fallback
                        students: scheduleParams.students || [],
                        status: 'SCHEDULED', 
                        scheduledAt: classDate.toISOString(),
                        createdAt: new Date().toISOString()
                    }));
                    generatedCount++;
                }
                currentDate.setDate(currentDate.getDate() + 1); // Next day
            }
            await Promise.all(promises);
            toast.success(`🎉 Boom! ${totalClasses} Classes Scheduled Successfully!`, { id: loadingId });
            setScheduleParams(null);
        } catch (error) {
            toast.error("Failed to schedule classes.", { id: loadingId });
        }
    };

    return (
        <div style={{display: 'flex', flexDirection: 'column', gap: '30px' }}>
            
            {/* 1️⃣ CREATE BATCH FORM */}
            <div style={{background: 'var(--bg-primary)', padding: '25px', borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.3)' }}>
                <h2 style={{color: 'var(--text-primary)', marginTop: 0, marginBottom: '20px' }}>✨ Create Smart Batch</h2>
                <form onSubmit={handleCreateBatch} style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                    
                    <div>
                        <label style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Batch Type</label>
                        <select value={batchType} onChange={e => setBatchType(e.target.value)} style={{width: '100%', padding: '12px', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid rgba(163, 177, 198, 0.4)' }}>
                            <option value="GROUP">🏫 Group Batch</option>
                            <option value="PRIVATE">👤 1-on-1 Private</option>
                        </select>
                    </div>

                    <div>
                        <label style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Chess Level</label>
                        <select value={level} onChange={e => setLevel(e.target.value)} style={{width: '100%', padding: '12px', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid rgba(163, 177, 198, 0.4)' }}>
                            {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                    </div>

                    <div>
                        <label style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Assign Coach</label>
                        <select value={coachEmail} onChange={e => setCoachEmail(e.target.value)} required style={{width: '100%', padding: '12px', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid rgba(163, 177, 198, 0.4)' }}>
                            <option value="">-- Select Coach --</option>
                            {coachesList.map(c => <option key={c.email} value={c.email}>{c.name}</option>)}
                        </select>
                    </div>

                    <div>
                        <label style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Class Time</label>
                        <input type="time" value={time} onChange={e => setTime(e.target.value)} required style={{width: '100%', padding: '12px', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid rgba(163, 177, 198, 0.4)', colorScheme: 'dark' }} />
                    </div>

                    <div style={{gridColumn: '1 / -1' }}>
                        <label style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Select Days</label>
                        <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            {WEEKDAYS.map(day => (
                                <div key={day} onClick={() => handleDayToggle(day)} style={{padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', background: selectedDays.includes(day) ? '#10b981' : 'var(--bg-primary)', color: selectedDays.includes(day) ? 'black' : 'var(--text-secondary)', border: '1px solid rgba(163, 177, 198, 0.4)', transition: '0.2s' }}>
                                    {day}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div style={{gridColumn: '1 / -1' }}>
                        <label style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Add Students (Emails separated by commas)</label>
                        <textarea value={studentsStr} onChange={e => setStudentsStr(e.target.value)} placeholder="student1@gmail.com, student2@gmail.com" style={{width: '100%', padding: '12px', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid rgba(163, 177, 198, 0.4)', minHeight: '60px', boxSizing: 'border-box' }} />
                    </div>

                    <div style={{gridColumn: '1 / -1', background: 'var(--bg-primary)', padding: '15px', borderRadius: '8px', border: batchType === 'PRIVATE' ? '1px dashed #f59e0b' : '1px dashed #10b981', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '15px' }}>
                        <div>
                            <div style={{color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>Auto-Generated Code:</div>
                            <div style={{color: batchType === 'PRIVATE' ? '#f59e0b' : '#10b981', fontSize: '18px', fontWeight: 'bold' }}>{generateBatchTitle()}</div>
                        </div>
                        <button disabled={isSubmitting} type="submit" style={{padding: '12px 30px', background: batchType === 'PRIVATE' ? '#f59e0b' : '#10b981', color: 'black', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px' }}>➕ Create Batch</button>
                    </div>
                </form>
            </div>

            {/* 2️⃣ BATCHES LIST */}
            {allBatches.length === 0 ? (
                <div style={{color: 'var(--text-secondary)', textAlign: 'center', padding: '40px', background: 'var(--bg-primary)', borderRadius: '12px', border: '1px dashed rgba(163, 177, 198, 0.4)' }}>No batches found. Create one to get started!</div>
            ) : (
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', marginTop: '10px' }}>
                    {allBatches.map(batch => {
                        const isPrivate = batch.batchType === 'PRIVATE';
                        const studentCount = (batch.students || []).length;
                        
                        // 🔥 SAFE FIX FOR CRASH
                        const rawCoach = batch.coachEmail || batch.coach || "No Coach";
                        const displayCoach = rawCoach.includes('@') ? rawCoach.split('@')[0] : rawCoach;
                        const displayDays = (batch.scheduleDays || []).join(', ') || 'No Days Configured';

                        return (
                        <div key={batch.id} style={{background: 'var(--bg-primary)', padding: '25px 20px', borderRadius: '12px', position: 'relative', boxShadow: '0 4px 15px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column' }}>
                            
                            {/* 🔥 TYPE BADGE */}
                            <div style={{position: 'absolute', top: '-12px', left: '15px', background: isPrivate ? '#f59e0b' : '#3b82f6', color: isPrivate ? 'black' : 'var(--text-primary)', fontSize: '11px', fontWeight: 'bold', padding: '4px 10px', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '5px', boxShadow: '0 4px 10px rgba(0,0,0,0.3)' }}>
                                {isPrivate ? '👑 1-ON-1 PRIVATE' : '🏫 GROUP BATCH'}
                            </div>

                            <button onClick={() => handleDelete(batch.id, batch.title)} style={{position: 'absolute', top: '15px', right: '15px', background: 'rgba(239, 68, 68, 0.1)', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px', width: '30px', height: '30px', borderRadius: '50%' }}>🗑️</button>
                            
                            <div style={{color: 'var(--text-primary)', fontSize: '18px', fontWeight: 'bold', marginBottom: '8px', marginTop: '5px', paddingRight: '30px' }}>{batch.title}</div>
                            
                            {/* 🔥 DETAILED SCHEDULE DISPLAY */}
                            <div style={{display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '15px', background: 'var(--bg-primary)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(163, 177, 198, 0.4)' }}>
                                <div style={{color: 'var(--text-secondary)', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>👨‍🏫 <span style={{color: 'var(--text-primary)'}}>{displayCoach}</span></div>
                                <div style={{color: '#10b981', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold' }}>📅 {displayDays}</div>
                                <div style={{color: '#3b82f6', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold' }}>⏱️ {formatTimeRange(batch.scheduleTime)}</div>
                                <div style={{color: 'var(--text-secondary)', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>👥 {studentCount} Students Enrolled</div>
                            </div>

                            {/* 🔥 SMART SCHEDULER BUTTON */}
                            <button 
                                onClick={() => setScheduleParams(batch)}
                                style={{width: '100%', marginTop: 'auto', padding: '12px', background: 'linear-gradient(90deg, #10b981, #059669)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', boxShadow: '0 4px 10px rgba(16, 185, 129, 0.3)' }}
                            >
                                🗓️ Schedule Classes
                            </button>
                        </div>
                    )})}
                </div>
            )}

            {/* 3️⃣ SMART BULK SCHEDULER MODAL */}
            {scheduleParams && (
                <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, backdropFilter: 'blur(5px)' }}>
                    <div style={{background: 'var(--bg-primary)', padding: '30px', borderRadius: '20px', width: '450px', maxWidth: '90%', border: '2px solid #10b981', boxShadow: '0 20px 50px rgba(0,0,0,0.5)' }}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h2 style={{color: '#10b981', margin: 0 }}>🗓️ Smart Scheduler</h2>
                            <button onClick={() => setScheduleParams(null)} style={{background: 'transparent', border: 'none', color: '#ef4444', fontSize: '20px', cursor: 'pointer' }}>✖</button>
                        </div>

                        <div style={{color: 'var(--text-secondary)', marginBottom: '20px', fontSize: '14px', lineHeight: '1.5' }}>
                            Scheduling for: <strong style={{color: 'var(--text-primary)'}}>{scheduleParams.title}</strong><br/>
                            Configured Days: <strong style={{color: '#3b82f6'}}>{(scheduleParams.scheduleDays || []).join(', ') || 'None'}</strong> @ {scheduleParams.scheduleTime}
                        </div>

                        <label style={{display: 'block', color: 'var(--text-primary)', marginBottom: '5px', fontWeight: 'bold' }}>How many classes to generate?</label>
                        <input type="number" min="1" max="144" value={scheduleConfig.totalClasses} onChange={(e) => setScheduleConfig({...scheduleConfig, totalClasses: parseInt(e.target.value) || 1})} style={{width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid rgba(163, 177, 198, 0.4)', background: 'var(--bg-primary)', color: 'var(--text-primary)', marginBottom: '20px', boxSizing: 'border-box', fontSize: '16px' }} />

                        <label style={{display: 'block', color: 'var(--text-primary)', marginBottom: '5px', fontWeight: 'bold', fontSize: '13px' }}>Start Schedule From Date:</label>
                        <input type="date" value={scheduleConfig.startDate} onChange={(e) => setScheduleConfig({...scheduleConfig, startDate: e.target.value})} style={{width: '100%', padding: '10px', marginBottom: '30px', borderRadius: '8px', border: '1px solid rgba(163, 177, 198, 0.4)', background: 'var(--bg-primary)', color: 'var(--text-primary)', boxSizing: 'border-box' }} />

                        <button onClick={generateSmartSchedule} style={{width: '100%', padding: '15px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)' }}>
                            🚀 Auto-Generate {scheduleConfig.totalClasses} Classes
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
